/**
 * Display shape for CALL engagements (HubSpot call sync-forward, coffee#4959).
 * The call specifics live in Engagement.metadata, written by
 * hubspot_call_to_engagement: direction, disposition(_label), call_status,
 * duration_ms, recording_url, from_number, to_number.
 */
export type CallDirection = "INBOUND" | "OUTBOUND";

export interface CallRow {
  id: string;
  title: string;
  direction: CallDirection | null;
  /** Outcome label as configured in HubSpot (e.g. "Connected", or a custom outcome). */
  outcome: string | null;
  durationMs: number | null;
  startTime: string;
  notesHtml: string;
  recordingUrl: string | null;
  fromNumber: string | null;
  toNumber: string | null;
}

function str(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v : null;
}

/** Map a transformed engagement list item (or raw engagement node) to a CallRow. */
export function toCallRow(eng: Record<string, unknown>): CallRow {
  const meta = (eng.metadata && typeof eng.metadata === "object" ? eng.metadata : {}) as Record<string, unknown>;
  const direction = str(meta.direction)?.toUpperCase();
  const duration = Number(meta.duration_ms);
  const recording = str(meta.recording_url) ?? str(eng.link);
  return {
    id: String(eng.id ?? ""),
    title: str(eng.displayName) ?? "",
    direction: direction === "INBOUND" || direction === "OUTBOUND" ? direction : null,
    outcome: str(meta.disposition_label),
    durationMs: Number.isFinite(duration) && duration > 0 ? duration : null,
    startTime: str(eng.startTime) ?? "",
    notesHtml: str(eng.description) ?? "",
    recordingUrl: recording && /^https?:\/\//i.test(recording) ? recording : null,
    fromNumber: str(meta.from_number),
    toNumber: str(meta.to_number),
  };
}

/** "0:42", "12:05", "1:02:03". Locale-neutral, so no i18n needed. */
export function formatCallDuration(ms: number | null): string {
  if (ms == null || !Number.isFinite(ms) || ms <= 0) return "";
  const total = Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}
